import { DateTime } from "luxon";
import { THEME_SETTINGS } from "@theme-settings";
import {
	UserAccountLockDurationTypesModel,
	UserAccountLockReasonTypesModel,
	UserOasisLockBlockReasonsTypesModel,
} from "@api-types";
import { FieldValues } from "react-hook-form";

export const lookupsInitialData = {
	durationAbrvs: THEME_SETTINGS.accountLockDurations,
	reasonAbrvs: [
		"other-reasons",
		"no-longer-interested-in-sports-betting",
		"gambling-addiction-prevention",
		"not-satisfied-with-sports-betting-services",
	],
};

export const lookupsInitialDataOasisEnabled = {
	durationAbrvs: [
		"30-days",
		"3-months",
		"4-months",
		"6-months",
		"8-months",
		"12-months",
		"permanent",
	],
	reasonAbrvs: [
		"other-reasons",
		"not-satisfied-with-sports-betting-services",
		"no-longer-interested-in-sports-betting",
	],
};

export const oasisLockLookupsInitialData = {
	oasisDurationAbrvs: [
		"permanent",
		"3-months",
		"4-months",
		"6-months",
		"8-months",
		"1-year",
	],
	oasisReasonCodes: ["01", "03", "04", "02"],
	oasisReasonCodesDisableCode: "99",
};

export const mapArray = (
	dataArray:
		| UserAccountLockReasonTypesModel[]
		| UserAccountLockDurationTypesModel[]
) => {
	if (!dataArray) return;
	const mapThroughDataArray = dataArray.map((data) => {
		return {
			id: data.id,
			name: data.name,
		};
	});
	return mapThroughDataArray;
};

export function sortArray(array: UserAccountLockDurationTypesModel[]) {
	array.sort(function (a, b) {
		if (a.units == -1 || b.units == -1) {
			return b.units - a.units;
		}
		return 0;
	});
	return array;
}

interface ChangeIdToNameModel {
	filteredData: {
		name: string;
		value: any;
	}[];
	reasonTypes: UserAccountLockReasonTypesModel[];
	sortDurationTypes: UserAccountLockDurationTypesModel[];
}

export const changeIdToName = (data: ChangeIdToNameModel) => {
	const { filteredData, reasonTypes, sortDurationTypes } = data;

	const changedData = filteredData.map((x) => {
		const getName =
			reasonTypes.find((item) => item.id === x.value) ||
			sortDurationTypes.find((duration) => duration.id === x.value);
		if (getName && getName.name) {
			x.value = getName.name;
		}
		return x;
	});

	return changedData;
};

export const formatDateTime = (date: string) => {
	return DateTime.fromISO(date).toLocaleString(
		DateTime.DATETIME_SHORT_WITH_SECONDS
	);
};

export const processDirtyFields = (
	data: FieldValues,
	dirtyFields: Partial<
		Readonly<{
			[x: string]: string;
		}>
	>
) => {
	const touchedFields: Partial<{
		[x: string]: any;
	}> = {};
	Object.keys(dirtyFields).map((fieldName) => {
		touchedFields[fieldName] = data[fieldName];
	});
	return touchedFields;
};

export function sortCheckboxArray(
	array: UserOasisLockBlockReasonsTypesModel[]
) {
	// codes are in string format like these = 04, 03, 02, 01
	// because of that they can be converted to numbers so we get 4, 3, 2, 1
	const sortedCheckboxList = array.sort(function (a, b) {
		return Number(a.code) - Number(b.code);
	});
	return sortedCheckboxList;
}

export const mapDataForPasswordConfirmation = (
	data: FieldValues,
	dirtyFields: Partial<
		Readonly<{
			[x: string]: any;
		}>
	>,
	reasonTypes: UserAccountLockReasonTypesModel[],
	sortDurationTypes: UserAccountLockDurationTypesModel[]
) => {
	const processedData = processDirtyFields(data, dirtyFields);
	const dataEntries = Object.entries(processedData);

	let modifiedEntry;
	const mappedData = dataEntries.map((entry) => {
		const modifiedEntryStart = entry[0].slice(0, 4);
		const modifiedEntryEnd = entry[0].slice(4);
		if (modifiedEntryStart.toLowerCase() == "lock") {
			modifiedEntry =
				modifiedEntryStart + "_" + modifiedEntryEnd + "_LABEL";
		} else if (entry[0] === "isPromoApproved") {
			return null;
		} else {
			modifiedEntry = entry[0].toUpperCase() + "_LABEl";
		}
		return {
			name: "account-lock:" + modifiedEntry.toUpperCase(),
			value: entry[1],
		};
	});
	const filteredData = mappedData.filter((x) => x != null);
	const getIDValues = changeIdToName({
		filteredData,
		reasonTypes,
		sortDurationTypes,
	});
	return getIDValues;
};

export const legalLockTranslationKeys = [
	"account-lock:ACCOUNT_LOCK_INFO_MSG_FIRST",
	"account-lock:ACCOUNT_LOCK_INFO_MSG_SECOND",
	"account-lock:ACCOUNT_LOCK_INFO_MSG_THIRD",
	"account-lock:ACCOUNT_LOCK_INFO_MSG_FOURTH",
	"account-lock:ACCOUNT_LOCK_INFO_MSG_FIFTH",
];
